import { mapDeviceAuthenticationFromSysApi } from '@shure/shared/angular/data-access/device-authentication';
import {
	LightingColor,
	LightingMode,
	LightingState,
	RgbColorMix
} from '@shure/shared/angular/data-access/system-api/core';
import {
	mapDeviceModelFromSysApi,
	mapValueOrDefault,
	mapDeviceConferencingOperationModeFromSysApi,
	mapChannelGroupFromSysApi,
	mapMicStatusFromSysApi,
	mapTransmitterMuteBehaviorFromSysApi
} from '@shure/shared/angular/data-access/system-api/mappers';
import {
	DeviceStatus,
	DeviceTransmitterMuteBehavior,
	LightBehavior,
	LightColor
} from '@shure/shared/angular/data-access/system-api/models';

import { LightsChannel, LightsDevice, RfChannelFeatures } from '../../api';
import {
	DeviceFragment,
	LightsAudioChannelFragment,
	LightsDeviceRfChannelFragment
} from '../graphql/generated/lights.generated';

import {
	mapAudioMuteControlFunctionFromSysApi,
	mapErrorIndicatorStateFromSysApi,
	mapLightingAutomixGatingFromSysApi,
	mapLightingColorFromSysApi,
	mapLightingModeFromSysApi,
	mapLightingStateFromSysApi,
	mapLightRingStyleFromSysApi,
	mapMicPolarPatternFromSysApi,
	mapTransmitterLightingBehaviorFromSysApi
} from './light.mapper';

export function mapLightsDeviceFromSysApi(
	fragment: DeviceFragment,
	authenticated: boolean | false,
	readonlyModeSelected: boolean | false
): LightsDevice {
	const description = fragment.description;
	const features = fragment.features;

	const sortedUnmuteModes = sortLightBehaviors(description.constraints.lightingUnmuteBehavior?.mode.options);
	const sortedMuteModes = sortLightBehaviors(description.constraints.lightingMuteBehavior?.mode.options);

	const sortedUnmuteColors = sortLightColors(description.constraints.lightingUnmuteBehavior?.color.options);
	const sortedMuteColors = sortLightColors(description.constraints.lightingMuteBehavior?.color.options);

	return {
		id: fragment.id,
		status: fragment.status,
		model: mapDeviceModelFromSysApi(fragment.description.model),
		virtual: fragment.virtual,
		name: mapValueOrDefault(fragment.features.name?.name),
		lightingConstraints: {
			lightingBrightness: {
				min: description.constraints.lightingBrightness?.level.range.min ?? 0,
				max: description.constraints.lightingBrightness?.level.range.max ?? 5,
				step: description.constraints.lightingBrightness?.level.range.step ?? 1
			},
			lightingMuteBehavior: {
				validColors: sortedMuteColors || [],
				validModes: sortedMuteModes || []
			},
			lightingUnmuteBehavior: {
				validColors: sortedUnmuteColors || [],
				validModes: sortedUnmuteModes || []
			},
			lightRing: {
				options:
					description.constraints.lightingRing?.style.options.map((option) =>
						mapLightRingStyleFromSysApi(option)
					) || []
			},
			lightingAutomixGating: {
				gateOffBehaviors:
					description.constraints.lightingAutomixGating?.automixerInputDisengagedBehavior?.options.map(
						(option) => mapLightingAutomixGatingFromSysApi(option)
					) || []
			},
			transmitterLightingBehavior: {
				behaviors:
					description.constraints.transmitterLightingBehavior?.behavior?.options.map((option) =>
						mapTransmitterLightingBehaviorFromSysApi(option)
					) || []
			}
		},
		features: {
			channels: features.audioChannels?.audioChannels.map((channel) => mapLightsChannelFromSysApi(channel)) ?? [],
			lightingBrightness: description.features.lightingBrightness
				? {
						level: features.lightingBrightness ? features.lightingBrightness.level : 0,
						supportedLevels: features.lightingBrightness?.supportedLevels ?? 6,
						isMissing: features.lightingBrightness === null
					}
				: undefined,
			ethernetLights: description.features.ethernetLights.supported
				? {
						enabled: features.ethernetLights ? features.ethernetLights.enabled : false,
						isMissing: features.ethernetLights === null
					}
				: undefined,
			errorIndicator: {
				state: mapErrorIndicatorStateFromSysApi(features.errorIndicator?.state),
				isMissing: !description.features.errorIndicator.supported
			},
			lightingMuteBehavior: description.features.lightingMuteBehavior.supported
				? {
						mode: features.lightingMuteBehavior
							? mapLightingModeFromSysApi(features.lightingMuteBehavior.mode)
							: LightBehavior.Off,
						color: features.lightingMuteBehavior
							? mapLightingColorFromSysApi(features.lightingMuteBehavior.color)
							: LightColor.Orange,
						isMissing: features.lightingMuteBehavior === null
					}
				: undefined,
			lightingUnmuteBehavior: description.features.lightingUnmuteBehavior.supported
				? {
						mode: features.lightingUnmuteBehavior
							? mapLightingModeFromSysApi(features.lightingUnmuteBehavior.mode)
							: LightBehavior.Off,
						color: features.lightingUnmuteBehavior
							? mapLightingColorFromSysApi(features.lightingUnmuteBehavior.color)
							: LightColor.Orange,
						isMissing: features.lightingUnmuteBehavior === null
					}
				: undefined,
			audioMute: description.features.audioMute.supported
				? {
						muted: features.audioMute ? features.audioMute.muted : false,
						isMissing: features.audioMute === null
					}
				: undefined,
			lightingCurrentBehavior: description.features.lightingBehavior.supported
				? {
						mode: features.lightingBehavior
							? mapLightingModeFromSysApi(features.lightingBehavior.mode)
							: LightBehavior.Off,
						color: features.lightingBehavior
							? mapLightingColorFromSysApi(features.lightingBehavior.color)
							: LightColor.Orange,
						state:
							features.lightingBehavior && features.lightingBehavior.state
								? mapLightingStateFromSysApi(features.lightingBehavior.state)
								: LightingState.Unmuted,
						isMissing: features.lightingBehavior === null
					}
				: undefined,
			identify: description.features.identify.supported
				? {
						isIdentifying: features.identify?.identifying ?? false,
						isMissing: features.identify === null
					}
				: undefined,
			lightingRingStyle: description.features.lightingRing.supported
				? {
						style: mapLightRingStyleFromSysApi(features.lightingRing?.style),
						isMissing: features.lightingRing === null
					}
				: undefined,
			lightingSegmentsBehavior: description.features.lightingSegmentsBehavior.supported
				? features.lightingSegmentsBehavior?.segments.map((segment) => ({
						mode: segment.mode,
						color: <RgbColorMix>segment.color
					}))
				: undefined,
			automixGating: description.features.lightingAutomixGating.supported
				? {
						enabled: features.lightingAutomixGating?.enabled ?? false,
						gateOffBehavior: mapLightingAutomixGatingFromSysApi(
							features.lightingAutomixGating?.automixerInputDisengagedBehavior
						),
						isMissing: features.lightingAutomixGating === null
					}
				: undefined,

			authentication: mapDeviceAuthenticationFromSysApi(
				features.authentication,
				authenticated,
				readonlyModeSelected
			),
			audioMuteControl: description.features.audioMuteControl.supported
				? {
						function: mapAudioMuteControlFunctionFromSysApi(features.audioMuteControl?.function),
						isMissing: features.audioMuteControl === null
					}
				: undefined,
			transmitterLightingBehavior: description.features.transmitterLightingBehavior.supported
				? {
						behavior: mapTransmitterLightingBehaviorFromSysApi(
							features.transmitterLightingBehavior?.behavior
						),
						isMissing: features.transmitterLightingBehavior === null
					}
				: undefined,
			configurationLock: description.features.configurationLock.supported
				? {
						enabled: features.configurationLock?.enabled ?? false,
						isMissing: features.configurationLock === null
					}
				: undefined,
			linkButtonLock: description.features.linkButtonLock.supported
				? {
						enabled: features.linkButtonLock?.enabled ?? false,
						isMissing: features.linkButtonLock === null
					}
				: undefined,
			conferencingOperationMode: description.features.conferencingOperationMode.supported
				? {
						mode: mapDeviceConferencingOperationModeFromSysApi(features.conferencingOperationMode?.mode),
						isMissing: features.conferencingOperationMode === null
					}
				: undefined,
			chargerBays: description.features.chargerBays.supported
				? [
						...(features.chargerBays?.chargerBays.map(
							(slot) => slot.features.dockedDevice?.device?.id ?? ''
						) ?? [])
					]
				: undefined,
			lightingAudioActivity: description.features.lightingAudioActivity.supported
				? {
						enabled: features.lightingAudioActivity?.enabled ?? false,
						isMissing: features.lightingAudioActivity === null
					}
				: undefined,
			transmitterMuteBehavior: description.features.transmitterMuteBehavior.supported
				? {
						isMissing: !features.transmitterMuteBehavior,
						muteBehavior: features.transmitterMuteBehavior
							? mapTransmitterMuteBehaviorFromSysApi(features.transmitterMuteBehavior.muteBehavior)
							: DeviceTransmitterMuteBehavior.LocalMuteAll
					}
				: undefined
		}
	};
}

export function mapLightsChannelFromSysApi(fragment: LightsAudioChannelFragment): LightsChannel {
	return {
		id: fragment.id,
		group: mapChannelGroupFromSysApi(fragment.description.groupName),
		features: {
			audioEnabled: fragment.description.features.lobeConfiguration.supported
				? {
						enabled: fragment.features.lobeConfiguration?.enabled ?? false,
						isMissing: fragment.features.lobeConfiguration === null
					}
				: undefined,
			micPolarPattern: fragment.description.features.micPolarPattern.supported
				? {
						pattern: mapMicPolarPatternFromSysApi(fragment.features.micPolarPattern?.pattern),
						isMissing: fragment.features.micPolarPattern === null
					}
				: undefined,
			microphoneAngle: fragment.description.features.microphoneAngle.supported
				? {
						angle: fragment.features.microphoneAngle?.fixed.angle ?? 0,
						isMissing: fragment.features.microphoneAngle === null
					}
				: undefined,
			mute: fragment.description.features.mute.supported
				? {
						muted: fragment.features.mute?.muted ?? false,
						isMissing: fragment.features.mute === null
					}
				: undefined,
			identify: fragment.description.features.identify.supported
				? {
						isIdentifying: fragment.features.identify?.identifying ?? false,
						isMissing: fragment.features.identify === null
					}
				: undefined,
			lightingCurrentBehavior: fragment.description.features.lightingBehavior.supported
				? {
						mode: fragment.features.lightingBehavior
							? mapLightingModeFromSysApi(fragment.features.lightingBehavior.mode)
							: LightBehavior.Off,
						color: fragment.features.lightingBehavior
							? mapLightingColorFromSysApi(fragment.features.lightingBehavior.color)
							: LightColor.Orange,
						state: fragment.features.lightingBehavior
							? mapLightingStateFromSysApi(fragment.features.lightingBehavior.state)
							: LightingState.Unmuted,
						isMissing: fragment.features.lightingBehavior === null
					}
				: undefined,
			automixer: fragment.description.features.automixer.supported
				? {
						// this enabled feature is only supported by MXA310, EZCOM_MIC, so for other devices,
						// if automixer is supported, we can assume that automixer is always enabled,
						// so for this enabled property, set it true, like MXA320
						enabled: fragment.features.automixer?.enabled ?? true,
						isMissing: fragment.features.automixer === null
					}
				: undefined,
			rfChannel: fragment.features.carriedByRfChannels?.rfChannels.length
				? {
						// There will be only one rfChannel associated to one audio channel
						id:
							fragment.features.carriedByRfChannels?.rfChannels.length === 1
								? fragment.features.carriedByRfChannels.rfChannels[0].id
								: '',
						features: mapRfChannelFeaturesFromSysApi(fragment.features.carriedByRfChannels?.rfChannels[0]),
						isMissing: !fragment.description.features.carriedByRfChannels.supported
					}
				: undefined
		}
	};
}

const lightingBehaviorPriorityMap = new Map<LightingMode, number>([
	[LightingMode.On, 1],
	[LightingMode.Flashing, 2],
	[LightingMode.Off, 3]
]);

const lightingColorPriorityMap = new Map<LightingColor, number>([
	[LightingColor.Red, 1],
	[LightingColor.Orange, 2],
	[LightingColor.Gold, 3],
	[LightingColor.Yellow, 4],
	[LightingColor.YellowGreen, 5],
	[LightingColor.Green, 6],
	[LightingColor.Turquoise, 7],
	[LightingColor.PowderBlue, 8],
	[LightingColor.Cyan, 9],
	[LightingColor.SkyBlue, 10],
	[LightingColor.Blue, 11],
	[LightingColor.Purple, 12],
	[LightingColor.LightPurple, 13],
	[LightingColor.Violet, 14],
	[LightingColor.Orchid, 15],
	[LightingColor.Pink, 16],
	[LightingColor.White, 17]
]);

function sortLightBehaviors(behaviors: LightingMode[] | undefined): LightBehavior[] {
	if (behaviors === undefined) {
		return [];
	}

	const sortedBehaviors = behaviors
		.slice()
		.sort((a, b) => (getLightingModePriority(a) >= getLightingModePriority(b) ? 1 : -1));

	return sortedBehaviors.map((mode) => mapLightingModeFromSysApi(mode));
}

function sortLightColors(colors: LightingColor[] | undefined): LightColor[] {
	if (colors === undefined) {
		return [];
	}

	const sortedColors = colors
		.slice()
		.sort((a, b) => (getLightingColorPriority(a) >= getLightingColorPriority(b) ? 1 : -1));

	return sortedColors.map((color) => mapLightingColorFromSysApi(color));
}

function getLightingModePriority(mode: LightingMode): number {
	const priority = lightingBehaviorPriorityMap.get(mode);
	return priority ? priority : 0;
}

function getLightingColorPriority(color: LightingColor): number {
	const priority = lightingColorPriorityMap.get(color);
	return priority ? priority : 0;
}

function mapRfChannelFeaturesFromSysApi(rfChannelSysApi: LightsDeviceRfChannelFragment): RfChannelFeatures {
	const {
		description: { features },
		features: { linkedTransmitter }
	} = rfChannelSysApi;
	return {
		isLinked: !!linkedTransmitter?.linkage?.device,

		device: features.linkedTransmitter.supported
			? {
					status: linkedTransmitter?.linkage?.device
						? linkedTransmitter?.linkage?.device?.status
						: DeviceStatus.Offline,
					micState: mapMicStatusFromSysApi(linkedTransmitter?.linkage?.device?.features?.micStatusV2?.status),
					isMissing: !features.linkedTransmitter.supported,
					id: linkedTransmitter?.linkage?.device?.id ?? ''
				}
			: undefined
	};
}
